import React, { useEffect, useMemo, useState, useRef } from 'react';
import { clientsAPI } from '../../api/client';
import { getClientLogoUrl } from '../../lib/fileUrls';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, X, Upload, Building2, LayoutGrid, List, ToggleLeft, ToggleRight, Search, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import ArchiveButton from '../../components/ArchiveButton';
import ClientAccessModal from '../../components/clients/ClientAccessModal';

// #NumericFieldGuard — quita cualquier carácter que no sea dígito,
// espacio, paréntesis, guion o + (para soportar +52 (55) 1234-5678).
// Se usa en teléfonos, GPS, RFC numérico, etc.
const stripToPhone = (v) => (v || '').replace(/[^0-9+\s()-]/g, '');
const stripToDigits = (v) => (v || '').replace(/[^0-9]/g, '');

const GIROS = [
  'Corporativo', 'Deportes', 'Educación', 'Entretenimiento',
  'Estadios', 'Gobierno', 'Hotelería', 'Otro',
  'Recintos Feriales', 'Retail', 'Salud', 'Tecnología',
]; // Ya ordenado alfabéticamente

const CLIENT_SIZES = [
  { value: 'micro', label: 'Micro (1-10 empleados)' },
  { value: 'pequena', label: 'Pequeña (11-50 empleados)' },
  { value: 'mediana', label: 'Mediana (51-250 empleados)' },
  { value: 'grande', label: 'Grande (250+ empleados)' },
];

async function getClientMeta(cid) {
  try {
    const r = await apiClient.get(`/client-meta/${cid}`);
    return r.data;
  } catch { return {}; }
}
async function saveClientMeta(cid, data) {
  await apiClient.put(`/client-meta/${cid}`, data);
}
async function uploadLogo(cid, file) {
  const formData = new FormData();
  formData.append('file', file);
  const r = await apiClient.post(`/client-meta/${cid}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  // Backend returns { url } from Cloudinary — accept either shape
  return r.data?.url || r.data?.logo_url || null;
}

// Build a safe preview URL: pass through Cloudinary/full URLs unchanged,
// only prefix the API base for legacy relative paths like "/uploads/..."
function buildLogoPreviewUrl(maybeUrl) {
  if (!maybeUrl) return null;
  if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl;
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
  return `${apiBase}${maybeUrl.startsWith('/') ? '' : '/'}${maybeUrl}`;
}

function ClientModal({ client, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    razon_social: '',
    giro: '',
    tamano_cliente: '',
    address_structured: { state: '', municipality: '', street: '', neighborhood: '', postal_code: '' },
    // ─── Campos adicionales solicitados en #CirKqznd ───
    safety_responsible_name: '',
    safety_responsible_phone: '',
    emergency_24_7_phone: '',
    gps_coordinates: { lat: '', lng: '' },
    rfc: '',
    legal_representative: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    if (client?.id) {
      setForm({
        name: client.name || '',
        contact_email: client.contact_email || '',
        contact_phone: client.contact_phone || client.phone || '',
        website: client.website || '',
        razon_social: client.razon_social || '',
        giro: client.giro || '',
        tamano_cliente: client.tamano_cliente || client.tamano || '',
        address_structured: {
          state: client.address_structured?.state || client.address?.state || '',
          municipality: client.address_structured?.municipality || client.address?.municipality || '',
          street: client.address_structured?.street || (typeof client.address === 'string' ? client.address : client.address?.street || ''),
          neighborhood: client.address_structured?.neighborhood || client.address?.neighborhood || '',
          postal_code: client.address_structured?.postal_code || client.address?.postal_code || '',
        },
        // ─── Campos adicionales (#CirKqznd) ───
        safety_responsible_name: client.safety_responsible_name || '',
        safety_responsible_phone: client.safety_responsible_phone || '',
        emergency_24_7_phone: client.emergency_24_7_phone || '',
        gps_coordinates: {
          lat: client.gps_coordinates?.lat ?? '',
          lng: client.gps_coordinates?.lng ?? '',
        },
        rfc: client.rfc || '',
        legal_representative: client.legal_representative || '',
      });
      // Load meta (logo)
      getClientMeta(client.id).then(m => {
        if (m.razon_social && !client.razon_social) setForm(f => ({ ...f, razon_social: m.razon_social }));
        if (m.giro && !client.giro) setForm(f => ({ ...f, giro: m.giro }));
        if (m.tamano && !client.tamano_cliente) setForm(f => ({ ...f, tamano_cliente: m.tamano }));
        const logoSrc = m.logo_url || m.logo;
        const previewUrl = buildLogoPreviewUrl(logoSrc);
        if (previewUrl) setLogoPreview(previewUrl);
      });
    }
  }, [client?.id]);

  // #SlugRetirado — el slug ya no se captura: no lo consume ninguna ruta
  // del admin ni de la API, y el backend lo genera solo a partir del nombre.
  const handleName = (val) => {
    setForm((f) => ({ ...f, name: val }));
  };

  const handleAddressField = (field, value) => {
    setForm(f => ({
      ...f,
      address_structured: { ...f.address_structured, [field]: value },
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // ─── GPS coords: enviar solo si ambos lat y lng son números válidos ───
      const lat = parseFloat(form.gps_coordinates.lat);
      const lng = parseFloat(form.gps_coordinates.lng);
      const hasGps = !isNaN(lat) && !isNaN(lng);
      const body = {
        name: form.name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        website: form.website || null,
        razon_social: form.razon_social || null,
        giro: form.giro || null,
        tamano_cliente: form.tamano_cliente || null,
        address_structured: form.address_structured,
        // ─── Campos adicionales (#CirKqznd) ───
        safety_responsible_name: form.safety_responsible_name || null,
        safety_responsible_phone: form.safety_responsible_phone || null,
        emergency_24_7_phone: form.emergency_24_7_phone || null,
        gps_coordinates: hasGps ? { lat, lng } : null,
        rfc: form.rfc || null,
        legal_representative: form.legal_representative || null,
      };
      const savedClient = await onSave(body);
      const cid = savedClient?.id || client?.id;
      if (cid && logoFile) {
        try {
          await uploadLogo(cid, logoFile);
          toast.success('Logo subido correctamente');
        } catch (logoErr) {
          // Don't kill the whole save if only the logo upload fails — but DO
          // surface the error so the user knows the logo did not persist.
          const detail = logoErr?.response?.data?.detail || logoErr?.message || 'Error desconocido';
          toast.error(`Logo no se subió: ${detail}. Los demás datos sí se guardaron.`, { duration: 8000 });
          // eslint-disable-next-line no-console
          if (process.env.NODE_ENV !== 'production') console.error('[uploadLogo]', logoErr);
        }
      }
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="client-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-soem-navy sticky top-0">
          <h3 className="font-bold text-white">{client ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={20} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Logo */}
          <div>
            <label htmlFor="f-logo-de-la-empresa" className="soem-label block mb-2">Logo de la empresa</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center overflow-hidden bg-slate-50 cursor-pointer hover:border-soem-primary transition-colors"
                onClick={() => logoRef.current?.click()}>
                {logoPreview
                  ? <img src={logoPreview} className="w-full h-full object-contain" alt="logo" />
                  : <Building2 size={28} className="text-slate-300" />}
              </div>
              <div>
                <button type="button" onClick={() => logoRef.current?.click()}
                  className="soem-btn-secondary px-3 py-1.5 text-xs flex items-center gap-2">
                  <Upload size={12} /> Subir logo
                </button>
                <p className="text-xs text-slate-400 mt-1">PNG, JPG · Máx 5MB</p>
              </div>
              <input ref={logoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleLogoChange} />
            </div>
          </div>

          <div>
            <label htmlFor="f-nombre-de-la-empresa" className="soem-label block mb-1">Nombre de la empresa *</label>
            <input id="f-nombre-de-la-empresa" className={inputCls} value={form.name} onChange={(e) => handleName(e.target.value)} required data-testid="client-name-input" />
          </div>
          <div>
            <label htmlFor="f-razón-social" className="soem-label block mb-1">Razon Social</label>
            <input id="f-razón-social" className={inputCls} value={form.razon_social}
              onChange={e => setForm(f => ({ ...f, razon_social: e.target.value }))}
              placeholder="Ej: Grupo Eventrix S.A. de C.V." />
          </div>
          <div>
            <label htmlFor="f-giro-o-sector" className="soem-label block mb-1">Giro o Sector</label>
            <select id="f-giro-o-sector" className={inputCls} value={form.giro} onChange={e => setForm(f => ({ ...f, giro: e.target.value }))}>
              <option value="">— Seleccionar giro —</option>
              {GIROS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="f-tamaño-del-cliente" className="soem-label block mb-1">Tamano del Cliente</label>
            <select id="f-tamaño-del-cliente" className={inputCls} value={form.tamano_cliente} onChange={e => setForm(f => ({ ...f, tamano_cliente: e.target.value }))}>
              <option value="">— Seleccionar tamano —</option>
              {CLIENT_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="f-email-de-contacto" className="soem-label block mb-1">Email de contacto</label>
            <input id="f-email-de-contacto" type="email" className={inputCls} value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })} required data-testid="client-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-teléfono" className="soem-label block mb-1">Telefono</label>
              {/* #NumericFieldGuard — solo dígitos, espacios, paréntesis,
                  guiones y "+". inputMode="tel" muestra teclado numérico
                  en móvil. */}
              <input id="f-teléfono" className={inputCls} value={form.contact_phone || ''}
                inputMode="tel"
                placeholder="+52 55 1234 5678"
                onChange={(e) => setForm({ ...form, contact_phone: stripToPhone(e.target.value) })} />
            </div>
            <div>
              <label htmlFor="f-website" className="soem-label block mb-1">Sitio web</label>
              <input id="f-website" className={inputCls} value={form.website || ''} placeholder="https://..."
                onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
          </div>
          
          {/* Address Fields */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <p className="soem-label mb-3">Dirección</p>
            <div className="space-y-3">
              <div>
                <label htmlFor="f-estado" className="soem-label block mb-1">Estado <span className="text-red-500">*</span></label>
                <input id="f-estado" className={inputCls} value={form.address_structured?.state || ''}
                  onChange={(e) => handleAddressField('state', e.target.value)}
                  placeholder="Ej: Ciudad de México" />
              </div>
              <div>
                <label htmlFor="f-delegación-municipio" className="soem-label block mb-1">Delegacion / Municipio</label>
                <input id="f-delegación-municipio" className={inputCls} value={form.address_structured?.municipality || ''}
                  onChange={(e) => handleAddressField('municipality', e.target.value)}
                  placeholder="Ej: Álvaro Obregón" />
              </div>
              <div>
                <label htmlFor="f-calle-y-número" className="soem-label block mb-1">Calle y Numero</label>
                <input id="f-calle-y-número" className={inputCls} value={form.address_structured?.street || ''}
                  onChange={(e) => handleAddressField('street', e.target.value)}
                  placeholder="Ej: Avenida Industria Militar 476" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="f-colonia" className="soem-label block mb-1">Colonia</label>
                  <input id="f-colonia" className={inputCls} value={form.address_structured?.neighborhood || ''}
                    onChange={(e) => handleAddressField('neighborhood', e.target.value)}
                    placeholder="Ej: Lomas de Sotelo" />
                </div>
                <div>
                  <label htmlFor="f-código-postal" className="soem-label block mb-1">Codigo Postal</label>
                  <input id="f-código-postal" className={inputCls} value={form.address_structured?.postal_code || ''}
                    inputMode="numeric"
                    onChange={(e) => handleAddressField('postal_code', stripToDigits(e.target.value).slice(0, 5))}
                    placeholder="00000" maxLength={5} />
                </div>
              </div>
            </div>
          </div>
          {/* ─── Campos adicionales solicitados en #CirKqznd ─────────────── */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <p className="soem-label mb-3">Contactos y datos adicionales</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="f-responsable-seguridad" className="soem-label block mb-1">Responsable de Seguridad</label>
                  <input id="f-responsable-seguridad" className={inputCls} value={form.safety_responsible_name}
                    onChange={(e) => setForm({ ...form, safety_responsible_name: e.target.value })}
                    placeholder="Nombre del encargado en el sitio" />
                </div>
                <div>
                  <label htmlFor="f-responsable-tel" className="soem-label block mb-1">Tel. Responsable</label>
                  <input id="f-responsable-tel" className={inputCls} value={form.safety_responsible_phone}
                    inputMode="tel"
                    onChange={(e) => setForm({ ...form, safety_responsible_phone: stripToPhone(e.target.value) })}
                    placeholder="55 1234 5678" />
                </div>
              </div>
              <div>
                <label htmlFor="f-emergencia-24-7" className="soem-label block mb-1">Contacto de Emergencias 24/7</label>
                <input id="f-emergencia-24-7" className={inputCls} value={form.emergency_24_7_phone}
                  inputMode="tel"
                  onChange={(e) => setForm({ ...form, emergency_24_7_phone: stripToPhone(e.target.value) })}
                  placeholder="Telefono disponible las 24 horas" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="f-gps-lat" className="soem-label block mb-1">Coordenadas GPS — Lat (opcional)</label>
                  <input id="f-gps-lat" type="number" step="any" className={inputCls} value={form.gps_coordinates.lat}
                    onChange={(e) => setForm({ ...form, gps_coordinates: { ...form.gps_coordinates, lat: e.target.value } })}
                    placeholder="19.4326" />
                </div>
                <div>
                  <label htmlFor="f-gps-lng" className="soem-label block mb-1">Coordenadas GPS — Lng</label>
                  <input id="f-gps-lng" type="number" step="any" className={inputCls} value={form.gps_coordinates.lng}
                    onChange={(e) => setForm({ ...form, gps_coordinates: { ...form.gps_coordinates, lng: e.target.value } })}
                    placeholder="-99.1332" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="f-rfc" className="soem-label block mb-1">RFC / Tax ID</label>
                  <input id="f-rfc" className={`${inputCls} font-mono uppercase`} value={form.rfc}
                    onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                    placeholder="XAXX010101000" maxLength={13} />
                </div>
                <div>
                  <label htmlFor="f-rep-legal" className="soem-label block mb-1">Representante Legal</label>
                  <input id="f-rep-legal" className={inputCls} value={form.legal_representative}
                    onChange={(e) => setForm({ ...form, legal_representative: e.target.value })}
                    placeholder="Nombre de la persona facultada" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60" data-testid="client-save-btn">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [clientsMeta, setClientsMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  // #ClientPanelAccess — cliente cuyo modal de accesos al panel está
  // abierto. Se guarda el objeto completo para mostrar el nombre en el
  // encabezado sin volver a pedirlo al API.
  const [accessClient, setAccessClient] = useState(null);
  // #DashboardSearch — buscador estilo Eventos, filtra por nombre,
  // razón social, email, RFC. Vive client-side sobre la lista cargada.
  const [search, setSearch] = useState('');
  // Persisted view toggle (#OHsX5HoV) — Marco asked for a list mode so
  // the page is readable as the client base grows.
  const [view, setView] = useState(() => localStorage.getItem('clients_view') || 'cards');
  const changeView = (v) => { setView(v); localStorage.setItem('clients_view', v); };
  // #ClientPanelAccess — crear/resetear accesos toca POST /users, que el
  // backend restringe a super_admin; se gatea con isAdmin para no mostrar
  // un botón que siempre devolvería 403 a los líderes de proyecto.
  const { canDo, isAdmin } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const r = await clientsAPI.list();
      // #ChronoOrder — más reciente arriba. Backend ya envía sorted pero
      // mantenemos client-side como red de seguridad para entornos
      // legacy o respuestas cacheadas.
      const items = (r.data.items || [])
        .filter(c => !c.is_archived)
        .sort((a, b) => {
          const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bd - ad;
        });
      setClients(items);
      // Load meta for each client
      const metaMap = {};
      await Promise.all(items.map(async c => {
        try {
          const m = await getClientMeta(c.id);
          metaMap[c.id] = m;
        } catch { metaMap[c.id] = {}; }
      }));
      setClientsMeta(metaMap);
    } catch { toast.error('Error al cargar clientes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    let savedClient;
    if (modal?.id) {
      savedClient = { id: modal.id, ...(await clientsAPI.update(modal.id, form)).data };
      toast.success('Cliente actualizado');
    } else {
      savedClient = (await clientsAPI.create(form)).data;
      toast.success('Cliente creado');
    }
    load();
    return savedClient;
  };

  // #ClientActiveToggle — antes sólo se podía archivar un cliente; no
  // había manera de re-activar uno marcado como inactivo. Marco vio 2
  // clientes en "Inactivo" sin opción de revertirlos. Este toggle envía
  // PUT /clients/{id} con {is_active: !current} y refresca la lista.
  const toggleActive = async (client) => {
    const next = !client.is_active;
    if (!window.confirm(`¿${next ? 'Activar' : 'Desactivar'} cliente "${client.name}"?`)) return;
    try {
      await clientsAPI.update(client.id, { is_active: next });
      toast.success(next ? 'Cliente activado' : 'Cliente desactivado');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || '';

  // #DashboardSearch — filtra por nombre, razón social, email, teléfono,
  // RFC o representante legal. Búsqueda case-insensitive.
  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const meta = clientsMeta[c.id] || {};
      const fields = [
        c.name, c.contact_email, c.contact_phone, c.razon_social, c.rfc,
        c.legal_representative, c.safety_responsible_name, meta.razon_social, meta.giro,
      ].filter(Boolean).map((s) => String(s).toLowerCase());
      return fields.some((f) => f.includes(q));
    });
  }, [search, clients, clientsMeta]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Gestión de Clientes</h1>
          <p className="text-slate-500 text-sm mt-1">
            {filteredClients.length === clients.length
              ? `${clients.length} clientes registrados`
              : `${filteredClients.length} de ${clients.length} clientes`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* #DashboardSearch — buscador inline */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="pl-9 pr-8 py-2 border border-slate-300 rounded-sm text-sm w-60 focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Buscar cliente..."
              aria-label="Buscar clientes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="clients-search"
            />
            {search && (
              <button
                type="button"
                aria-label="Limpiar búsqueda"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
            <button onClick={() => changeView('cards')}
              className={`p-2 ${view === 'cards' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid="view-cards-btn" title="Vista tarjetas">
              <LayoutGrid size={15} />
            </button>
            <button onClick={() => changeView('table')}
              className={`p-2 ${view === 'table' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid="view-table-btn" title="Vista lista">
              <List size={15} />
            </button>
          </div>
          {canDo('create_client') && (
            <button onClick={() => setModal({})} className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="create-client-btn">
              <Plus size={16} /> Nuevo Cliente
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => <div key={i} className="soem-card h-40 animate-pulse bg-slate-100" />)}
        </div>
      ) : filteredClients.length === 0 ? (
        <p className="text-slate-400 text-center py-12">
          {search ? `No se encontraron clientes para "${search}".` : 'No hay clientes registrados.'}
        </p>
      ) : view === 'table' ? (
        <div className="soem-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Cliente</th>
                <th className="px-4 py-3 text-left">Razón social</th>
                <th className="px-4 py-3 text-left">Giro</th>
                <th className="px-4 py-3 text-left">Contacto</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredClients.map((c) => {
                const meta = clientsMeta[c.id] || {};
                const logoUrl = c.logo_url || meta.logo_url;
                return (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {logoUrl ? (
                          <img src={logoUrl.startsWith('http') ? logoUrl : `${API_BASE}${logoUrl}`} alt="logo" className="w-7 h-7 object-contain rounded-sm border border-slate-200 bg-white" onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="w-7 h-7 bg-slate-100 rounded-sm flex items-center justify-center border border-slate-200">
                            <Building2 size={14} className="text-slate-400" />
                          </div>
                        )}
                        <span className="font-semibold text-soem-navy">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{meta.razon_social || '—'}</td>
                    <td className="px-4 py-3">
                      {meta.giro
                        ? <span className="inline-flex text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">{meta.giro}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      <div>{c.contact_email || '—'}</div>
                      {c.contact_phone && <div className="text-xs text-slate-400">{c.contact_phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {c.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {canDo('create_client') && (
                          <button onClick={() => toggleActive(c)}
                            className={c.is_active ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-soem-primary'}
                            data-testid={`toggle-active-${c.id}`}
                            title={c.is_active ? 'Desactivar cliente' : 'Activar cliente'}>
                            {c.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => setAccessClient(c)} className="text-slate-400 hover:text-soem-primary"
                            data-testid={`client-access-${c.id}`} title="Accesos del panel">
                            <KeyRound size={15} />
                          </button>
                        )}
                        <button onClick={() => setModal(c)} className="text-slate-400 hover:text-soem-primary" data-testid={`edit-client-${c.id}`} title="Editar">
                          <Edit2 size={15} />
                        </button>
                        {canDo('create_client') && <ArchiveButton entity="clients" id={c.id} name={c.name} onArchived={load} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredClients.map((c) => {
            const meta = clientsMeta[c.id] || {};
            const logoUrl = c.logo_url || meta.logo_url;
            return (
              <div key={c.id} className="soem-card-accent p-5" data-testid={`client-card-${c.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img src={logoUrl.startsWith('http') ? logoUrl : `${API_BASE}${logoUrl}`} alt="logo" className="w-10 h-10 object-contain rounded-sm border border-slate-200 bg-white" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center border border-slate-200">
                        <Building2 size={20} className="text-slate-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-soem-navy">{c.name}</h3>
                      {meta.razon_social && <p className="text-slate-400 text-xs">{meta.razon_social}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {c.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                    {canDo('create_client') && (
                      <button onClick={() => toggleActive(c)}
                        className={c.is_active ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-soem-primary'}
                        data-testid={`toggle-active-${c.id}`}
                        title={c.is_active ? 'Desactivar cliente' : 'Activar cliente'}>
                        {c.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => setAccessClient(c)} className="text-slate-400 hover:text-soem-primary"
                        data-testid={`client-access-${c.id}`} title="Accesos del panel">
                        <KeyRound size={15} />
                      </button>
                    )}
                    <button onClick={() => setModal(c)} className="text-slate-400 hover:text-soem-primary" data-testid={`edit-client-${c.id}`}>
                      <Edit2 size={15} />
                    </button>
                    {canDo('create_client') && <ArchiveButton entity="clients" id={c.id} name={c.name} onArchived={load} />}
                  </div>
                </div>
                {meta.giro && (
                  <span className="inline-flex items-center text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium mb-2">
                    {meta.giro}
                  </span>
                )}
                <p className="text-sm text-slate-500">{c.contact_email}</p>
                {c.contact_phone && <p className="text-sm text-slate-500">{c.contact_phone}</p>}
              </div>
            );
          })}
        </div>
      )}

      {modal !== null && (
        <ClientModal client={modal?.id ? modal : null} onSave={handleSave} onClose={() => { setModal(null); load(); }} />
      )}

      {accessClient && (
        <ClientAccessModal client={accessClient} onClose={() => setAccessClient(null)} />
      )}
    </div>
  );
}
